/**
 * Automatic UV generation for scene objects.
 *
 * Uses box projection (triplanar mapping) — the industry standard for fast
 * unwrapping arbitrary geometry. For each vertex, the dominant normal axis
 * determines which two positional axes become the UV coordinates.
 *
 * This produces usable UVs for basic texturing (checkerboards, procedural
 * patterns, debug materials). It is not a full LSCM/ABF unwrap — a proper
 * UV editor can be layered on top later by letting users override the auto-
 * generated UVs stored in SceneObject.uvs.
 */

import type { PolygonTopology, SceneObject } from "./types.ts";

/**
 * Apply a geometry patch to a scene object, auto-regenerating UVs when
 * geometry (vertices, normals, indices, or topology) changes.
 *
 * Centralizes the "update geometry → regenerate UVs" pattern so that
 * individual edit operations don't repeat the box-projection call.
 */
export function patchWithUVs(
  obj: SceneObject,
  patch: Partial<SceneObject>,
): SceneObject {
  const updated = { ...obj, ...patch };
  if (patch.vertices || patch.indices || patch.loopNormals || patch.topology) {
    updated.uvs = unwrapBoxProject(
      updated.vertices,
      updated.loopNormals,
      updated.topology,
    );
  }
  return updated;
}

/**
 * Generate per-corner UVs via box projection (topology-aware).
 *
 * When a topology is provided, produces one UV pair per topology corner
 * (matching loopNormals layout). Each corner uses the shared vertex
 * position and its per-corner normal to select the projection plane.
 *
 * When no topology is provided (importer fallback), produces per-vertex
 * UVs assuming normals and positions have the same count.
 *
 * For each sample, the normal's dominant axis selects the projection plane:
 *   |Nx| largest → project (Y, Z)
 *   |Ny| largest → project (X, Z)
 *   |Nz| largest → project (X, Y)
 *
 * UVs are normalized to the object's bounding box so they span [0,1]
 * regardless of object size. The depth axis (perpendicular to the
 * projection plane) is used to offset the UV island, preventing overlap
 * when geometry has multiple layers at different depths (e.g. solidify).
 *
 * @param vertices  Flat [x,y,z, ...] shared vertex positions
 * @param normals   Flat [x,y,z, ...] per-corner normals (or per-vertex if no topology)
 * @param topology  Optional polygon topology — when present, output is per-corner
 * @returns         Flat [u,v, ...] Float32Array
 */
export function unwrapBoxProject(
  vertices: Float32Array,
  normals: Float32Array,
  topology?: PolygonTopology,
): Float32Array {
  const vertCount = vertices.length / 3;

  // Compute bounding box for normalization
  let minX = Infinity,
    minY = Infinity,
    minZ = Infinity;
  let maxX = -Infinity,
    maxY = -Infinity,
    maxZ = -Infinity;
  for (let i = 0; i < vertCount; i++) {
    const x = vertices[i * 3],
      y = vertices[i * 3 + 1],
      z = vertices[i * 3 + 2];
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  }
  const rangeX = maxX - minX || 1;
  const rangeY = maxY - minY || 1;
  const rangeZ = maxZ - minZ || 1;

  // Use a 3×2 grid so each of the 6 projection faces (+X, -X, +Y, -Y, +Z, -Z)
  // gets its own UV island, preventing opposite faces from sharing coordinates.
  const cellW = 1 / 3;
  const cellH = 1 / 2;
  const pad = 0.01;
  const innerW = cellW - pad * 2;
  const innerH = cellH - pad * 2;
  const depthScale = 0.05;

  /** Project a single position+normal sample into a UV pair. */
  function projectSample(
    px: number,
    py: number,
    pz: number,
    nx: number,
    ny: number,
    nz: number,
  ): [number, number] {
    const ax = Math.abs(nx),
      ay = Math.abs(ny),
      az = Math.abs(nz);
    let localU: number, localV: number;
    let col: number, row: number;
    let depthOffset: number;

    if (ax >= ay && ax >= az) {
      localU = (py - minY) / rangeY;
      localV = (pz - minZ) / rangeZ;
      depthOffset = ((px - minX) / rangeX) * depthScale;
      col = 0;
      row = nx >= 0 ? 0 : 1;
    } else if (ay >= ax && ay >= az) {
      localU = (px - minX) / rangeX;
      localV = (pz - minZ) / rangeZ;
      depthOffset = ((py - minY) / rangeY) * depthScale;
      col = 1;
      row = ny >= 0 ? 0 : 1;
    } else {
      localU = (px - minX) / rangeX;
      localV = (py - minY) / rangeY;
      depthOffset = ((pz - minZ) / rangeZ) * depthScale;
      col = 2;
      row = nz >= 0 ? 0 : 1;
    }

    return [
      col * cellW + pad + (localU + depthOffset) * innerW,
      (row === 0 ? cellH : 0) + pad + (localV + depthOffset) * innerH,
    ];
  }

  if (topology) {
    // Per-corner UVs: one UV pair per topology corner
    const corners = topology.corners;
    const cornerCount = corners.length;
    const uvs = new Float32Array(cornerCount * 2);
    for (let c = 0; c < cornerCount; c++) {
      const vi = corners[c];
      const [u, v] = projectSample(
        vertices[vi * 3],
        vertices[vi * 3 + 1],
        vertices[vi * 3 + 2],
        normals[c * 3],
        normals[c * 3 + 1],
        normals[c * 3 + 2],
      );
      uvs[c * 2] = u;
      uvs[c * 2 + 1] = v;
    }
    return uvs;
  }

  // Fallback: per-vertex UVs (for importers where normals.length === vertices.length)
  const uvs = new Float32Array(vertCount * 2);
  for (let i = 0; i < vertCount; i++) {
    const [u, v] = projectSample(
      vertices[i * 3],
      vertices[i * 3 + 1],
      vertices[i * 3 + 2],
      normals[i * 3],
      normals[i * 3 + 1],
      normals[i * 3 + 2],
    );
    uvs[i * 2] = u;
    uvs[i * 2 + 1] = v;
  }
  return uvs;
}

/**
 * Incrementally re-project UVs for corners whose vertex is in `affectedVertices`.
 * Mutates `uvs` in place. Mirrors `unwrapBoxProject`'s per-corner branch so
 * partial results are byte-identical to a full recompute on those corners.
 *
 * Bounding box is re-derived from the full `vertices` buffer — matches what a
 * full recompute would use, keeping the partial path consistent with any
 * earlier full unwrap stored on the object.
 *
 * Use when a small subset of vertices moved (e.g. single-vertex translate on
 * a large mesh) and rewriting every face's UVs would be O(F) for no benefit.
 */
export function unwrapBoxProjectPartial(
  vertices: Float32Array,
  normals: Float32Array,
  topology: PolygonTopology,
  affectedVertices: ReadonlySet<number>,
  uvs: Float32Array,
): void {
  const vertCount = vertices.length / 3;

  let minX = Infinity,
    minY = Infinity,
    minZ = Infinity;
  let maxX = -Infinity,
    maxY = -Infinity,
    maxZ = -Infinity;
  for (let i = 0; i < vertCount; i++) {
    const x = vertices[i * 3],
      y = vertices[i * 3 + 1],
      z = vertices[i * 3 + 2];
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  }
  const rangeX = maxX - minX || 1;
  const rangeY = maxY - minY || 1;
  const rangeZ = maxZ - minZ || 1;

  const cellW = 1 / 3;
  const cellH = 1 / 2;
  const pad = 0.01;
  const innerW = cellW - pad * 2;
  const innerH = cellH - pad * 2;
  const depthScale = 0.05;

  const corners = topology.corners;
  const cornerCount = corners.length;
  for (let c = 0; c < cornerCount; c++) {
    const vi = corners[c];
    if (!affectedVertices.has(vi)) continue;

    const px = vertices[vi * 3],
      py = vertices[vi * 3 + 1],
      pz = vertices[vi * 3 + 2];
    const nx = normals[c * 3],
      ny = normals[c * 3 + 1],
      nz = normals[c * 3 + 2];
    const ax = Math.abs(nx),
      ay = Math.abs(ny),
      az = Math.abs(nz);
    let localU: number, localV: number;
    let col: number, row: number;
    let depthOffset: number;

    if (ax >= ay && ax >= az) {
      localU = (py - minY) / rangeY;
      localV = (pz - minZ) / rangeZ;
      depthOffset = ((px - minX) / rangeX) * depthScale;
      col = 0;
      row = nx >= 0 ? 0 : 1;
    } else if (ay >= ax && ay >= az) {
      localU = (px - minX) / rangeX;
      localV = (pz - minZ) / rangeZ;
      depthOffset = ((py - minY) / rangeY) * depthScale;
      col = 1;
      row = ny >= 0 ? 0 : 1;
    } else {
      localU = (px - minX) / rangeX;
      localV = (py - minY) / rangeY;
      depthOffset = ((pz - minZ) / rangeZ) * depthScale;
      col = 2;
      row = nz >= 0 ? 0 : 1;
    }

    uvs[c * 2] = col * cellW + pad + (localU + depthOffset) * innerW;
    uvs[c * 2 + 1] =
      (row === 0 ? cellH : 0) + pad + (localV + depthOffset) * innerH;
  }
}
